import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentLanguage } from '../../features/langSlice';
import {
  useAddOmnisendEventMutation,
  useGetNutritionistsQuery,
  useUpdateQuizIngredientsMutation,
} from '../../features/quizApiSlice';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getProductForWebAction, getSubsActions } from '../../app/api/useDataManage/subscriptionApis';
import { useTranslation } from 'react-i18next';
import { getPersonalFormulaReadyText } from '../../utils/product-copy';
import {
  CustomerReviews,
  HereMainHeading,
  Faq,
  Vegan,
  GlutenFree,
  NoArtificialCloning,
  SuitableForAthletes,
  VeganVCP,
  BuiltByScience,
  CustomerReviewsDesc,
  HereMainHeading2,
  StartNowButton,
  EveryThreeMonths,
  Month,
} from '../../Constants';
import ProductSuggestionWhyRiise from './ProductSuggestionWhyRiise';
import { calculateIngredientPrice } from '../../utils/productSuggestions';
import { saveMetaParamsBeforeRedirect } from '../../utils';
import { sendOmnisendAddToCartEvent } from '../../utils/omnisend';
import { waitForTracking } from '../../utils/tracking';
import { getStripeLocale } from '../../utils/language';
import { buildReferralClientReference, getCheckoutReferralCode } from '../../utils/referral';
import AddIngredientModal from './components/AddIngredientModal';
import ExpertSection from './components/ExpertSection';
import useProduct from '../../hooks/useProduct';
import {
  useCustomFormulaMutation,
  useGetCustomFormulaQuery,
  useExistingFormulaMutation,
} from '../../features/customFormulaApiSlice';
import { toast } from 'react-toastify';
import ReviewsMobile from './components/ReviewsMobile';
import CustomFormulaForm from './components/CustomFormulaForm';
import Whatsapp from '../../assets/whatsapp.webp';
import Loader from '../elements/Loader';
import useMeta from '../../hooks/useMeta';
import Carousel from './components/Carousel';
import IngredientsModal from './components/IngredientsModal';
import EditFormulaModal from './components/EditFormulaModal';
import HowToEnjoy from './components/HowToEnjoy';
import HighStandards from '../Home/HighStandards';
import BoomTestimonials from './components/BoomTestimonials';
import MakeChoice from '../Home/MakeChoice';
import HereForYou from './components/HereForYou';
import VeganImg from '../../assets/vegan.webp';
import GlutenFreeImg from '../../assets/gluten-free.webp';
import NoArtificialColoringImg from '../../assets/no-additives.webp';
import SuitableForAthletesimg from '../../assets/suitable-for-athletes-doping-free.webp';
import DiscountBanner from '../Home/DiscountBanner';
import MobileSubscriptionCardV4 from '../MobileSubscriptionCardV4';
import ReviewsOverlay from '../Reviews';
import ResultYouFeel from './components/ResultYouFeel';
import Header from './product/Header';
import Omega3MediaCarousel from '../Omega3/components/Omega3MediaCarousel';
import CustomFormulaOmega3Purchase, {
  CUSTOM_FORMULA_OMEGA3_PLANS,
  getCustomFormulaOmega3PlanByKey,
  getCustomFormulaOmega3PlanByDuration,
} from './components/CustomFormulaOmega3Purchase';
import Omega3ProductSelector from './components/Omega3ProductSelector';
import Omega3DifferenceSection from '../Omega3/components/Omega3DifferenceSection';
import Omega3BenefitsSection from '../Omega3/components/Omega3BenefitsSection';
import Omega3HowToSection from '../Omega3/components/Omega3HowToSection';
import Omega3IngredientsSection from '../Omega3/components/Omega3IngredientsSection';
import Omega3Reviews from '../Omega3/components/Omega3Reviews';
import Omega3Faq from '../Omega3/components/Omega3Faq';
import { clearCachedAnswersWebResults } from '../../utils/answersWebCache';

const isValidCustomFormulaCheckoutReference = (checkoutDetails) =>
  checkoutDetails?.isGenericFormula
    ? Boolean(checkoutDetails?.formulaId)
    : Boolean(checkoutDetails?.formulaId && checkoutDetails?.userId);

const OMEGA3_DURATION_BY_FORMULA_DURATION = {
  '3 months': '1 month',
  '6 months': '3 months',
  '30 days': '30 days',
  '90 days': '90 days',
};

const OMEGA3_PRICE_BY_DURATION = {
  '1 month': 24.3,
  '3 months': 64.8,
  '30 days': 27,
  '90 days': 81,
};

const getOmega3DurationForFormulaDuration = (duration) =>
  OMEGA3_DURATION_BY_FORMULA_DURATION[duration] || duration;

const getOmega3PriceByDuration = (duration) => OMEGA3_PRICE_BY_DURATION[duration] || 0;

const getOmega3PriceForFormulaDuration = (duration) =>
  getOmega3PriceByDuration(getOmega3DurationForFormulaDuration(duration));

const formatEuro = (amount) => `${Number(amount || 0).toFixed(2)}€`;

const buildOmega3DisplayTotals = (formulaTotals, shouldIncludeOmega3) => {
  if (!shouldIncludeOmega3) return formulaTotals;

  return {
    oneMonth: Number(formulaTotals?.oneMonth || 0) + OMEGA3_PRICE_BY_DURATION['30 days'],
    threeMonths: Number(formulaTotals?.threeMonths || 0) + OMEGA3_PRICE_BY_DURATION['1 month'],
    sixMonths: Number(formulaTotals?.sixMonths || 0) + OMEGA3_PRICE_BY_DURATION['3 months'] / 3,
    threeMonthsOneOff: Number(formulaTotals?.threeMonthsOneOff || 0) + OMEGA3_PRICE_BY_DURATION['90 days'],
  };
};

const CustomFormula = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formula_id, setFormulaId] = useState(searchParams.get('formula_id'));

  const {
    onInputChange,
    removeIngredientFromBin,
    addIngredient,
    removeIngredient,
    onRemoveQuantity,
    onAddQuantity,
    onSelectPlan,
    buildPaymentLinkCheckoutUrl,
    preloadCheckoutSessions,
    resolveCheckoutUrl,
    getFormulaChanges,
    isOneOffCheckout: isOneOffCheckoutDuration,
    allIngredients,
    allIngredientPrices,
    lang,
    data,
    setData,
    ingredients,
    setIngredients,
    hasFormulaChanged,
    setHasFormulaChanged,
    error,
    setError,
    prices,
    setPrices,
    pricesData,
    setPricesData,
    totals,
    setTotals,
    continent,
    paymentLink,
    setPaymentLink,
    isCheckoutLoading,
    subscribtion,
    setSubscription,
    token,
    addIngredientsModal,
    setAddIngredientsModal,
    newSubs,
    setNewSubs,
    ONE_OFF_SUB,
    THREE_MONTHS_ONE_OFF_SUB,
    isCreateCheckoutSessionLoading,
  } = useProduct({ autoPreloadCheckoutSessions: false });

  const selectedLang = useSelector(selectCurrentLanguage);
  const { subs } = useSelector((state) => state.subs);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [discount_code, setDiscountCode] = useState('');
  const [note, setNote] = useState('');
  const [nutritionist, setNutritionist] = useState('');
  const [nutritionistName, setNutritionistName] = useState('');
  const [nutritionistData, setNutritionistData] = useState('');
  const [practitionerCode, setPractitionerCode] = useState('');
  const [isGeneric, setIsGeneric] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');
  const [isExistingFormula, setIsExistingFormula] = useState(false);
  const [hasOmega3Selected, setHasOmega3Selected] = useState(false);
  const [ingredientsModal, setIngredientsModal] = useState(false);
  const [editIngredientsModal, setEditIngredientsModal] = useState(false);
  const [isPastMobileSubs, setIsPastMobileSubs] = useState(false);
  const [reviewData, setReviewData] = useState();
  const boxRef = useRef(null);
  const formRef = useRef();
  const sectionRefMobile = useRef(null);
  const mobileSubsRef = useRef();
  const leftContainerRef = useRef(null);
  const rightScrollRef = useRef(null);

  // All queries and mutations
  const [updateQuizIngredientsQuantity, { isLoading: updateQuizLoading }] =
    useUpdateQuizIngredientsMutation();
  const [customFormulaMutation, { isLoading: isCreateCustomFormulaLoading }] = useCustomFormulaMutation();
  const {
    data: customFormula,
    refetch,
    isLoading: isGetCustomFormulaLoading,
  } = useGetCustomFormulaQuery({ formulaId: formula_id, lang: selectedLang });
  const { data: nutritionists, isLoading: getNutritionistsLoading } = useGetNutritionistsQuery();
  const [existingFormula, { isLoading: existingFormulaLoading }] = useExistingFormulaMutation();

  const loading =
    updateQuizLoading ||
    isCreateCustomFormulaLoading ||
    isGetCustomFormulaLoading ||
    getNutritionistsLoading ||
    existingFormulaLoading;
  const isShareableFormula = Boolean(customFormula?.customFormula?.is_generic);
  const isOneOffCheckout = isOneOffCheckoutDuration(subscribtion?.duration);
  const savedIngredientCount = Array.isArray(customFormula?.customFormula?.ingredients)
    ? customFormula.customFormula.ingredients.length
    : 0;
  const hasFormulaProduct = data.length > 0 || savedIngredientCount > 0;
  const hasSavedIngredientlessRecommendation = Boolean(
    formula_id &&
    customFormula?.customFormula &&
    Array.isArray(customFormula.customFormula.ingredients) &&
    savedIngredientCount === 0,
  );
  const hasOmega3Recommendation =
    Boolean(customFormula?.customFormula?.omega3_recommended) || hasSavedIngredientlessRecommendation;
  const hasSelectedOmega3Product = Boolean(hasOmega3Recommendation && hasOmega3Selected);
  const isOmega3OnlyRecommendation = Boolean(hasOmega3Recommendation && !hasFormulaProduct);
  const hasAnyCheckoutProduct = hasFormulaProduct || hasSelectedOmega3Product;
  const canUseServerCheckout = Boolean(
    formula_id && customFormula?.customFormula && (!isOneOffCheckout || hasSelectedOmega3Product),
  );
  const hasCheckoutErrors = Object.values(error).some((value) => value === true);
  const isCheckoutDisabled =
    loading ||
    isCheckoutLoading ||
    isCreateCheckoutSessionLoading ||
    hasCheckoutErrors ||
    !hasAnyCheckoutProduct ||
    (!canUseServerCheckout && !paymentLink);
  const hasOmega3PlanSelection = String(subscribtion?.subId || '').startsWith('omega3_');
  const selectedOmega3Plan = getCustomFormulaOmega3PlanByDuration(subscribtion?.duration);
  const selectedOmega3PlanKey = selectedOmega3Plan?.id || CUSTOM_FORMULA_OMEGA3_PLANS[0].id;

  const handleOmega3OnlyPlanSelect = useCallback(
    (planKey) => {
      const plan = getCustomFormulaOmega3PlanByKey(planKey);

      setSubscription((currentSubscription) => ({
        ...currentSubscription,
        title: t(plan.titleKey),
        duration: plan.duration,
        subId: `omega3_${plan.id}`,
        description: t(plan.quantityLabelKey),
        expirey: new Date().toLocaleDateString(),
      }));
    },
    [setSubscription, t],
  );

  // ALl USE EFFECTS HERE
  useEffect(() => {
    if (subs) {
      const tempSubs = subs.slice(1, subs.length);
      const sub = [...tempSubs, ONE_OFF_SUB, THREE_MONTHS_ONE_OFF_SUB];
      // const sub = [ONE_OFF_SUB, ...tempSubs];
      setNewSubs(sub);
    }
  }, [subs, setNewSubs, ONE_OFF_SUB, THREE_MONTHS_ONE_OFF_SUB]);

  useEffect(() => {
    if (!formula_id) return;
    refetch();
  }, [formula_id, refetch]);

  useEffect(() => {
    if (!formula_id || !customFormula?.customFormula || !nutritionists?.nutritionists) return;
    const nutri = nutritionists?.nutritionists?.find(
      (item) => item.code === customFormula?.customFormula?.nutritionist,
    );
    setNutritionistName(nutri?.name);
  }, [formula_id, customFormula, nutritionists?.nutritionists]);

  useEffect(() => {
    if (!practitionerCode || !nutritionists?.nutritionists) return;
    const nutri = nutritionists?.nutritionists?.find((item) => item.code === practitionerCode);
    setNutritionistData(nutri);
  }, [practitionerCode, nutritionists?.nutritionists]);

  useEffect(() => {
    if (!formula_id) return;
    if (customFormula && customFormula?.customFormula) {
      const tempData = (customFormula?.customFormula?.ingredients || []).map((item) => {
        const ingredient = allIngredients?.data?.find((i) => i?.id === item?.ingredient_id);
        return {
          daily_dose: item?.daily_dose,
          description: item?.description || ingredient?.description,
          ingredient: item?.ingredient || ingredient?.ingredient,
          ingredient_id: item?.ingredient_id,
          per_eu_nrv: item?.per_eu_nrv,
          id: item?.id,
        };
      });
      setData(tempData);
      setIngredients(tempData);
      setHasOmega3Selected(
        Boolean(customFormula?.customFormula?.omega3_recommended) || tempData.length === 0,
      );
      localStorage.setItem('quiz_id', customFormula?.customFormula?.quiz_id);
      if (customFormula?.customFormula?.email) {
        localStorage.setItem('email', customFormula?.customFormula?.email);
      } else {
        localStorage.removeItem('email');
      }
    }
  }, [formula_id, customFormula, allIngredients, setData, setIngredients]);

  useEffect(() => {
    dispatch(getSubsActions(selectedLang ?? 'en'));
    dispatch(getProductForWebAction());
  }, [dispatch, selectedLang, token]);

  // This will calculate the initial prices for the ingredients
  useEffect(() => {
    if (!ingredients || !allIngredientPrices?.data?.length || !formula_id) return;
    setPrices({});
    customFormula?.customFormula?.ingredients?.forEach((item) => {
      const ingredient = allIngredients?.data?.find((i) => i?.id === item?.ingredient_id);
      if (parseFloat(item.daily_dose) > 0) {
        calculateIngredientPrice(
          { ...item, ingredient: ingredient?.ingredient },
          parseFloat(item.daily_dose),
          setPrices,
          setPaymentLink,
          setSubscription,
          allIngredientPrices,
        );
        calculateIngredientPrice(
          { ...item, ingredient: ingredient?.ingredient },
          parseFloat(item.daily_dose),
          setPricesData,
          setPaymentLink,
          setSubscription,
          allIngredientPrices,
        );
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingredients, allIngredientPrices]);

  // THis will calculate the price for the product
  useEffect(() => {
    const totalSum = Object.values(prices).reduce((sum, value) => sum + parseFloat(value), 0);
    const shipping = totalSum >= 55 ? 0 : continent === 'eu' ? 6 : 12;

    setTotals({
      oneMonth: totalSum + shipping,
      threeMonths: totalSum * 0.9, // 10% off of 1 month price + shipping
      sixMonths: totalSum * 0.7, // 30% off of 1 month price, free shipping
      threeMonthsOneOff: totalSum * 0.8 * 3 + shipping, // 20% off of 1 month price + shipping
    });
  }, [prices, continent, setTotals]);

  useEffect(() => {
    if (newSubs.length === 0) return;
    if (isOmega3OnlyRecommendation) return;

    const oneMonthSub = newSubs.find((sub) => sub?.duration === '3 months');
    if (oneMonthSub) {
      onSelectPlan(oneMonthSub);
    }
  }, [isOmega3OnlyRecommendation, newSubs, onSelectPlan, totals]);

  useEffect(() => {
    if (!isOmega3OnlyRecommendation || hasOmega3PlanSelection) return;

    handleOmega3OnlyPlanSelect(CUSTOM_FORMULA_OMEGA3_PLANS[0].id);
  }, [handleOmega3OnlyPlanSelect, hasOmega3PlanSelection, isOmega3OnlyRecommendation]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const reviewsCount = document.querySelectorAll('.cssVar-subheading__number');
      if (reviewsCount) {
        const reviews = [];
        reviewsCount.forEach((element) => {
          reviews.push(element.innerHTML);
        });
        setReviewData(reviews);
      }
    });

    // Start observing the target node for DOM changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Clean up the observer when the component unmounts
    return () => observer.disconnect();
  }, []);

  // Ingredients observer
  useEffect(() => {
    if (!mobileSubsRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isVisible = entry.isIntersecting;
        const isScrolledPast = !isVisible && entry.boundingClientRect.top < 0;

        setIsPastMobileSubs(isScrolledPast);
      },
      { threshold: 0.1 },
    );

    observer.observe(mobileSubsRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  const onSaveFormula = (onlySave) => {
    if (
      (!isGeneric && (!email || !name || !practitionerCode)) ||
      (isGeneric && (!name || !practitionerCode))
    ) {
      toast.error(
        isGeneric
          ? 'Please enter a formula title and practitioner code'
          : 'Please enter name, email and practitioner code',
      );
      return;
    }
    if (data.length === 0 && !hasOmega3Selected) {
      toast.error('Please add at least one ingredient or Omega-3');
      return;
    }

    customFormulaMutation({
      name,
      email,
      data,
      nutritionist: practitionerCode,
      note,
      discount_code,
      partner_id: nutritionistData?.partner_id,
      onlySave: isGeneric ? true : onlySave,
      is_generic: isGeneric,
      omega3_recommended: hasOmega3Selected,
    })
      .then((res) => {
        toast.success('Formula saved successfully');
        navigate('/custom-formula/success', { state: res });
      })
      .catch((err) => {
        console.log('ERROR => ', err);
      });
  };
  const [addOmnisendEventMutation] = useAddOmnisendEventMutation();

  const getCustomFormulaCheckoutDetails = useCallback(
    (formula) => {
      const isGenericFormula = Boolean(formula?.is_generic);
      const isOneOff = isOneOffCheckoutDuration(subscribtion?.duration);
      const hasFormula = data.length > 0;
      const hasOmega3 = Boolean(hasOmega3Recommendation && hasOmega3Selected);
      const isOmega3Only = hasOmega3 && !hasFormula;
      const omega3CheckoutDuration = isOmega3Only
        ? subscribtion?.duration
        : getOmega3DurationForFormulaDuration(subscribtion?.duration);
      const checkoutDuration = isOmega3Only ? omega3CheckoutDuration : subscribtion?.duration;
      const referralCode = getCheckoutReferralCode();
      const formulaId = formula?.id;
      const userId = formula?.user_id;
      const baseClientReferenceId = isGenericFormula ? `cf_${formulaId}` : `cfu_${formulaId}_${userId}`;
      const clientReferenceId =
        isOneOff && referralCode
          ? buildReferralClientReference(baseClientReferenceId, referralCode)
          : baseClientReferenceId;
      const promoCode = isOneOff ? '' : referralCode;
      const locale = getStripeLocale(selectedLang);
      const email = isGenericFormula ? null : formula?.email;
      const url = buildPaymentLinkCheckoutUrl({
        payLink: paymentLink,
        email,
        clientReferenceId,
        promoCode,
        locale,
      });

      return {
        email,
        userId,
        formulaId,
        quizId: formula?.quiz_id,
        clientReferenceId,
        promoCode,
        locale,
        url,
        isGenericFormula,
        shouldUseServerCheckout: Boolean(formula_id && (!isOneOff || hasOmega3)),
        checkoutDuration,
        productType: isOmega3Only ? 'omega-3' : 'riise_formula',
        selectedAmount: isOmega3Only ? getOmega3PriceByDuration(omega3CheckoutDuration) : undefined,
        includeOmega3: hasOmega3 && hasFormula,
      };
    },
    [
      buildPaymentLinkCheckoutUrl,
      data.length,
      formula_id,
      hasOmega3Recommendation,
      hasOmega3Selected,
      isOneOffCheckoutDuration,
      paymentLink,
      selectedLang,
      subscribtion?.duration,
    ],
  );

  const preloadCustomFormulaSubscriptionCheckouts = useCallback(
    ({ force = false } = {}) => {
      const formula = customFormula?.customFormula;
      if (editIngredientsModal && !force) return Promise.resolve([]);
      if (hasFormulaChanged && !force) return Promise.resolve([]);
      if (!formula_id || !formula) return Promise.resolve([]);

      const checkoutDetails = getCustomFormulaCheckoutDetails(formula);
      if (
        !checkoutDetails.shouldUseServerCheckout ||
        !checkoutDetails.clientReferenceId ||
        !isValidCustomFormulaCheckoutReference(checkoutDetails)
      ) {
        return Promise.resolve([]);
      }

      return preloadCheckoutSessions(
        {
          email: checkoutDetails.email,
          clientReferenceId: checkoutDetails.clientReferenceId,
          promoCode: checkoutDetails.promoCode,
          locale: checkoutDetails.locale,
          quizId: checkoutDetails.quizId,
          productType: checkoutDetails.productType,
          selectedAmount: checkoutDetails.selectedAmount,
          includeOmega3: checkoutDetails.includeOmega3,
        },
        { force },
      );
    },
    [
      customFormula?.customFormula,
      editIngredientsModal,
      formula_id,
      getCustomFormulaCheckoutDetails,
      hasFormulaChanged,
      preloadCheckoutSessions,
    ],
  );

  useEffect(() => {
    preloadCustomFormulaSubscriptionCheckouts();
  }, [preloadCustomFormulaSubscriptionCheckouts]);

  const waitForCheckoutTracking = ({ email, url }) =>
    waitForTracking([
      sendOmnisendAddToCartEvent({
        addOmnisendEventMutation,
        email,
        url,
        subscribtion,
        totals,
      }),
    ]);

  const redirectToCheckout = async ({ email, url }) => {
    await waitForCheckoutTracking({ email, url });
    saveMetaParamsBeforeRedirect();
    window.location.href = url;
  };

  const onSubmitData = async () => {
    const formula = customFormula?.customFormula;
    const checkoutDetails = getCustomFormulaCheckoutDetails(formula);
    const { email } = checkoutDetails;

    if (!isValidCustomFormulaCheckoutReference(checkoutDetails)) {
      toast.error('Problem preparing this custom formula for checkout');
      return;
    }

    if (!checkoutDetails.shouldUseServerCheckout && !checkoutDetails.url) {
      toast.error('Problem preparing this custom formula for checkout');
      return;
    }

    try {
      const url = await resolveCheckoutUrl({
        duration: checkoutDetails.checkoutDuration,
        shouldUseServerCheckout: checkoutDetails.shouldUseServerCheckout,
        paymentLinkCheckoutUrl: checkoutDetails.url,
        checkoutOverrides: {
          email: checkoutDetails.email,
          clientReferenceId: checkoutDetails.clientReferenceId,
          promoCode: checkoutDetails.promoCode,
          locale: checkoutDetails.locale,
          quizId: checkoutDetails.quizId,
          productType: checkoutDetails.productType,
          selectedAmount: checkoutDetails.selectedAmount,
          includeOmega3: checkoutDetails.includeOmega3,
        },
      });

      if (checkoutDetails.isGenericFormula) {
        saveMetaParamsBeforeRedirect();
        window.location.href = url;
        return;
      }

      if (hasFormulaChanged) {
        const { filteredObjects, addedItems, removedItems } = getFormulaChanges();
        const shouldSaveIngredients =
          filteredObjects.length > 0 || addedItems.length > 0 || removedItems.length > 0;

        if (!shouldSaveIngredients) {
          setHasFormulaChanged(false);
          await redirectToCheckout({ email, url });
          return;
        }

        await Promise.all([
          updateQuizIngredientsQuantity({
            updatedItems: filteredObjects,
            addedItems,
            removedItems,
            quizId: customFormula?.customFormula?.quiz_id,
          }).unwrap(),
          waitForCheckoutTracking({ email, url }),
        ]);
        // Same reason as the product-suggestion checkout path: the cached
        // /quiz/answersweb result is now stale, and nothing else invalidates it.
        clearCachedAnswersWebResults();
        setIngredients(data);
        setHasFormulaChanged(false);
        saveMetaParamsBeforeRedirect();
        window.location.href = url;
        return;
      }

      await redirectToCheckout({ email, url });
    } catch (err) {
      toast.error(err?.data?.message || err?.message || 'Something went wrong');
      console.log('err => ', err);
    }
  };

  const loadExistingFormula = () => {
    if (!practitionerCode || !customerEmail) {
      toast.error('Please enter practitioner code and customer email');
      return;
    } else {
      existingFormula({
        nutritionist: practitionerCode,
        email: customerEmail,
      })
        .then((data) => {
          if (data?.data?.customFormula) {
            setFormulaId(data.data?.customFormula?.id);
            setName(data.data?.customFormula?.name);
            setEmail(data.data?.customFormula?.email);
            setNote(data.data?.customFormula?.note);
            setDiscountCode(data.data?.customFormula?.discount_code);
            setIsGeneric(false);
            setIsExistingFormula(true);
          } else if (data?.error?.data) {
            toast.error(data.error.data.message);
          }
        })
        .catch((error) => {
          console.log('Error => ', error);
        });
    }
  };

  const { metaEvent } = useMeta();

  const ICONS_DATA = [
    { title: t(Vegan), img: VeganImg },
    { title: t(GlutenFree), img: GlutenFreeImg },
    { title: t(NoArtificialCloning), img: NoArtificialColoringImg },
    { title: t(SuitableForAthletes), img: SuitableForAthletesimg },
  ];

  const isBannerVisible = useMemo(() => isPastMobileSubs, [isPastMobileSubs]);

  useEffect(() => {
    if (!ingredientsModal) return;

    window.history.pushState({ modal: true }, '');

    const handlePopState = () => {
      setIngredientsModal(false);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [ingredientsModal]);

  const scrollToSectionEndMobile = useCallback(() => {
    if (sectionRefMobile.current) {
      sectionRefMobile.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  }, []);

  // Handle wheel events on left container to scroll right container
  useEffect(() => {
    const leftContainer = leftContainerRef.current;
    const right = rightScrollRef.current;
    if (!leftContainer || !right) return;

    const handleLeftWheel = (e) => {
      if (window.innerWidth < 1024) return;

      if (!leftContainer.contains(e.target)) {
        return;
      }

      const atTop = right.scrollTop <= 0;
      const atBottom = right.scrollTop + right.clientHeight >= right.scrollHeight - 1;

      if ((atTop && e.deltaY < 0) || (atBottom && e.deltaY > 0)) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      right.scrollTop += e.deltaY;
    };

    leftContainer.addEventListener('wheel', handleLeftWheel, { passive: false, capture: true });

    return () => {
      leftContainer.removeEventListener('wheel', handleLeftWheel, { capture: true });
    };
  }, []);

  const getPriceByDuration = (duration, totals, multiplier = 1) => {
    const priceMap = {
      '6 months': totals.sixMonths,
      '3 months': totals.threeMonths,
      '90 days': totals.threeMonthsOneOff,
      '1 month': totals.oneMonth,
      '30 days': totals.oneMonth,
    };
    return (parseFloat(priceMap[duration] || 0) * multiplier).toFixed(2);
  };

  const displayTotals = useMemo(
    () => buildOmega3DisplayTotals(totals, hasSelectedOmega3Product),
    [hasSelectedOmega3Product, totals],
  );
  const totalPrice = Math.round(
    getPriceByDuration(
      subscribtion?.duration,
      hasSelectedOmega3Product ? displayTotals : totals,
      subscribtion?.duration === '6 months' ? 3 : 1,
    ),
  );

  const showStrikeThrough =
    hasFormulaProduct && ['6 months', '3 months', '90 days'].includes(subscribtion?.duration);
  const getButtonPriceText = () => {
    if (subscribtion?.duration === '30 days' || subscribtion?.duration === '90 days') return `${totalPrice}€`;
    if (subscribtion?.duration === '6 months') return `${totalPrice}€ ${t(EveryThreeMonths)}`;
    return `${totalPrice}€ /${t(Month)}`;
  };
  const combinedOmega3Price = getOmega3PriceForFormulaDuration(subscribtion?.duration);
  const certificationText = hasOmega3Recommendation
    ? t('CustomFormula_Omega3CertificationLine')
    : t(VeganVCP);
  const omega3OnlyMetadata = customFormula?.customFormula ? (
    <div className="t-flex t-flex-col t-gap-0">
      {nutritionistName && (
        <h5 className="t-text-base md:t-text-lg t-text-gray-500">
          {t('CustomFormula_PractitionerPrefix')}{' '}
          <span className="!t-font-bold t-text-black">{nutritionistName}</span>
        </h5>
      )}
      {customFormula?.customFormula?.note && (
        <h5 className="t-text-base md:t-text-lg t-text-gray-500">
          {t('CustomFormula_NotePrefix')}{' '}
          <span className="!t-font-bold t-text-black">{customFormula?.customFormula?.note}</span>
        </h5>
      )}
      {customFormula?.customFormula?.discount_code && (
        <h5 className="t-text-base md:t-text-lg t-text-gray-500">
          {t('CustomFormula_DiscountCodePrefix')}{' '}
          <span className="!t-font-bold t-text-black">{customFormula?.customFormula?.discount_code}</span>
        </h5>
      )}
    </div>
  ) : null;

  return (
    <>
      <Header />
      <div className="">
        <DiscountBanner />
      </div>

      <div
        className="product-suggestion-section section-padding t-flex t-flex-col md:t-flex-row t-items-center t-gap-8"
        style={{ paddingTop: '10px' }}
      >
        <div className="t-relative t-w-full t-max-w-[1320px] lg:t-h-screen t-mx-auto t-flex t-flex-col md:t-flex-row t-gap-8 lg:t-gap-0 t-py-4 !t-px-4 lg:!t-px-8">
          <div
            ref={leftContainerRef}
            className="lg:t-sticky lg:t-top-0 t-w-full sm:t-max-w-[300px] lg:t-max-w-none lg:t-w-1/2  hide-scrollbar"
            style={{ overflow: 'hidden' }}
          >
            {(!formula_id || isExistingFormula) && (
              <div className="t-flex t-flex-col t-gap-2 mb-4">
                <h3 className="!t-m-0 !t-text-secondary !t-font-extrabold" style={{ fontFamily: 'Roboto' }}>
                  {t('CustomFormula_LoginHeading')}
                </h3>
                <div className="!t-w-full md:!t-w-1/2 t-flex t-flex-col t-items-start t-gap-0 position-relative">
                  <label className="t-text-gray-600 !t-ml-0">
                    {t('CustomFormula_PractitionerCodeLabel')}
                  </label>
                  <input
                    type="text"
                    name="practitionerCode"
                    id="practitionerCode"
                    placeholder={t('CustomFormula_PractitionerCodePlaceholder')}
                    className={`form-control t-h-[40px] !t-pl-3`}
                    value={practitionerCode}
                    onChange={(e) => setPractitionerCode(e.target.value)}
                    required
                    minLength={3}
                  />
                </div>

                <h3 className="!t-m-0 !t-text-secondary !t-font-extrabold" style={{ fontFamily: 'Roboto' }}>
                  {t('CustomFormula_LoadExistingHeading')}
                </h3>
                <div className="!t-w-full md:!t-w-1/2 t-flex t-flex-col t-items-start t-gap-0 position-relative">
                  <label className="t-text-gray-600 !t-ml-0">{t('CustomFormula_CustomerEmailLabel')}</label>
                  <input
                    type="text"
                    name="customerEmail"
                    id="customerEmail"
                    placeholder={t('CustomFormula_CustomerEmailPlaceholder')}
                    className={`form-control t-h-[40px] !t-pl-3`}
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    required
                    minLength={3}
                  />
                </div>
                <button
                  className="btn !t-py-1 !t-w-[160px] !t-mx-auto text-lowercase btn-custom-quaternary !t-ml-0 !t-rounded-md"
                  disabled={loading}
                  onClick={loadExistingFormula}
                >
                  {t('CustomFormula_LoadFormulaButton')}
                </button>
              </div>
            )}

            {customFormula?.customFormula && (
              <div className="lg:t-hidden t-flex t-flex-col t-gap-0">
                {nutritionistName && (
                  <h5 className="t-text-base md:t-text-lg t-text-gray-500">
                    {t('CustomFormula_PractitionerPrefix')}{' '}
                    <span className="!t-font-bold t-text-black">{nutritionistName}</span>
                  </h5>
                )}
                {customFormula?.customFormula?.note && (
                  <h5 className="t-text-base md:t-text-lg t-text-gray-500">
                    {t('CustomFormula_NotePrefix')}{' '}
                    <span className="!t-font-bold t-text-black">{customFormula?.customFormula?.note}</span>
                  </h5>
                )}
                {customFormula?.customFormula?.discount_code && (
                  <h5 className="t-text-base md:t-text-lg t-text-gray-500">
                    {t('CustomFormula_DiscountCodePrefix')}{' '}
                    <span className="!t-font-bold t-text-black">
                      {customFormula?.customFormula?.discount_code}
                    </span>
                  </h5>
                )}
              </div>
            )}
            {isOmega3OnlyRecommendation ? (
              <Omega3MediaCarousel />
            ) : (
              <Carousel
                data={data}
                allIngredients={allIngredients}
                onClickViewFormula={() => setIngredientsModal(true)}
                name={customFormula?.customFormula ? customFormula?.customFormula?.name.trim() : 'Custom'}
                isCustomFormula
              />
            )}
          </div>
          <div
            ref={rightScrollRef}
            className="sm:t-w-[calc(100%-300px)] lg:t-w-1/2 lg:t-pl-8 t-flex t-flex-col t-gap-1.5 lg:t-overflow-y-auto lg:t-h-full hide-scrollbar"
            style={{ overscrollBehavior: 'auto' }}
          >
            {isOmega3OnlyRecommendation ? (
              <CustomFormulaOmega3Purchase
                selectedPlanKey={selectedOmega3PlanKey}
                onSelectPlan={handleOmega3OnlyPlanSelect}
                onCheckout={onSubmitData}
                checkoutDisabled={isCheckoutDisabled}
                reviewCount={reviewData?.[1]?.trim() ?? 0}
                reviewAverage={reviewData?.[0]?.trim() ?? 0}
                onReviewClick={() =>
                  document.getElementById('omega3-reviews')?.scrollIntoView({ behavior: 'smooth' })
                }
                metadata={omega3OnlyMetadata}
              />
            ) : (
              <>
                {customFormula?.customFormula && (
                  <div className="t-flex t-flex-col t-gap-2">
                    <h2
                      className="!t-m-0 !t-text-secondary !t-font-extrabold"
                      style={{ fontFamily: 'Roboto' }}
                    >
                      {getPersonalFormulaReadyText(t, customFormula?.customFormula?.name, i18n.language)}
                    </h2>
                    <ReviewsOverlay
                      totalReviews={reviewData?.[1]?.trim() ?? 0}
                      averageReviews={reviewData?.[0]?.trim() ?? 0}
                      onClick={scrollToSectionEndMobile}
                    />
                    <div className="t-hidden lg:t-flex t-flex-col t-gap-0">
                      {nutritionistName && (
                        <h5 className="t-text-base md:t-text-lg t-text-gray-500">
                          {t('CustomFormula_PractitionerPrefix')}{' '}
                          <span className="!t-font-bold t-text-black">{nutritionistName}</span>
                        </h5>
                      )}
                      {customFormula?.customFormula?.note && (
                        <h5 className="t-text-base md:t-text-lg t-text-gray-500">
                          {t('CustomFormula_NotePrefix')}{' '}
                          <span className="!t-font-bold t-text-black">
                            {customFormula?.customFormula?.note}
                          </span>
                        </h5>
                      )}
                      {customFormula?.customFormula?.discount_code && (
                        <h5 className="t-text-base md:t-text-lg t-text-gray-500">
                          {t('CustomFormula_DiscountCodePrefix')}{' '}
                          <span className="!t-font-bold t-text-black">
                            {customFormula?.customFormula?.discount_code}
                          </span>
                        </h5>
                      )}
                    </div>
                    <p
                      className="!t-m-0 t-text-center md:t-text-left t-italic"
                      style={{ fontFamily: 'Roboto' }}
                    >
                      {certificationText}
                    </p>
                  </div>
                )}

                <Omega3ProductSelector
                  t={t}
                  hasFormula={hasFormulaProduct}
                  hasOmega3={hasOmega3Recommendation}
                  hasOmega3Selected={hasOmega3Selected}
                  onOmega3Change={setHasOmega3Selected}
                  omega3Price={formatEuro(combinedOmega3Price)}
                  showOmega3Selector={Boolean(formula_id && !isExistingFormula)}
                />

                {(!formula_id || isExistingFormula) && (
                  <div className="t-hidden md:t-flex !t-w-full">
                    <CustomFormulaForm
                      name={name}
                      setName={setName}
                      email={email}
                      setEmail={setEmail}
                      isGeneric={isGeneric}
                      setIsGeneric={setIsGeneric}
                      onSaveFormula={onSaveFormula}
                      note={note}
                      setNote={setNote}
                      discount_code={discount_code}
                      setDiscountCode={setDiscountCode}
                      nutritionist={nutritionist}
                      setNutritionist={setNutritionist}
                      nutritionists={nutritionists?.nutritionists}
                      isCreateCustomFormulaLoading={loading}
                      showGenericOption={!isExistingFormula}
                      hasOmega3Recommended={hasOmega3Selected}
                      setHasOmega3Recommended={setHasOmega3Selected}
                    />
                  </div>
                )}
                <div ref={mobileSubsRef}>
                  <MobileSubscriptionCardV4
                    username="Custom Formula"
                    subscribtion={subscribtion}
                    subs={newSubs}
                    onSelectPlan={onSelectPlan}
                    totals={totals}
                    paymentLink={paymentLink}
                    canCheckoutWithoutPaymentLink={canUseServerCheckout}
                    isCheckoutLoading={loading || isCheckoutLoading || isCreateCheckoutSessionLoading}
                    onSubmitData={onSubmitData}
                    error={error}
                    boxRef={boxRef}
                    showBtn={!!formula_id}
                    totalIngredients={data.length}
                    ingredients={data}
                    canShowStrikeThrough={hasFormulaProduct}
                    ctaPriceTextOverride={hasSelectedOmega3Product ? getButtonPriceText() : null}
                    ctaStrikeThroughPriceOverride={
                      hasSelectedOmega3Product
                        ? ['6 months', '90 days'].includes(subscribtion?.duration)
                          ? Math.round(parseFloat(displayTotals.oneMonth || 0) * 3)
                          : Math.round(parseFloat(displayTotals.oneMonth || 0))
                        : null
                    }
                    omega3AddOnPrice={hasSelectedOmega3Product ? combinedOmega3Price : 0}
                    showOmega3Benefit={hasSelectedOmega3Product}
                  />
                </div>
                {(!formula_id || isExistingFormula) && (
                  <div className="md:t-hidden" ref={formRef}>
                    <CustomFormulaForm
                      name={name}
                      setName={setName}
                      email={email}
                      setEmail={setEmail}
                      isGeneric={isGeneric}
                      setIsGeneric={setIsGeneric}
                      onSaveFormula={onSaveFormula}
                      note={note}
                      setNote={setNote}
                      discount_code={discount_code}
                      setDiscountCode={setDiscountCode}
                      nutritionist={nutritionist}
                      setNutritionist={setNutritionist}
                      nutritionists={nutritionists?.nutritionists}
                      isCreateCustomFormulaLoading={loading}
                      showGenericOption={!isExistingFormula}
                      hasOmega3Recommended={hasOmega3Selected}
                      setHasOmega3Recommended={setHasOmega3Selected}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {!!formula_id && (
          <div
            className="t-flex t-flex-col t-items-center t-justify-center t-gap-2 md:t-hidden t-fixed t-bottom-0 t-bg-white t-z-40 t-w-full t-mx-auto t-py-4 "
            style={{ boxShadow: '10px 10px 20px 10px #efefef' }}
            hidden={!isBannerVisible || !hasAnyCheckoutProduct || isOmega3OnlyRecommendation}
          >
            <button
              className="btn !t-w-fit text-capitalize btn-custom-quaternary !t-rounded-lg"
              onClick={onSubmitData}
              disabled={isCheckoutDisabled}
            >
              {t(StartNowButton)} -{' '}
              <span className="!t-m-0 t-text-white !t-font-bold ">
                {showStrikeThrough && (
                  <span className="t-line-through">
                    {['6 months', '90 days'].includes(subscribtion?.duration)
                      ? Math.round(parseFloat(totals.oneMonth || 0) * 3)
                      : Math.round(parseFloat(totals.oneMonth || 0))}
                    €
                  </span>
                )}{' '}
                {getButtonPriceText()}
              </span>
            </button>
          </div>
        )}
        {!formula_id && (
          <div
            className="t-flex t-flex-col t-items-center t-justify-center t-gap-2 md:t-hidden t-fixed t-bottom-0 t-bg-white t-z-40 t-w-full t-mx-auto t-py-4 "
            style={{ boxShadow: '10px 10px 20px 10px #efefef' }}
          >
            <button
              className="btn !t-w-fit text-lowercase btn-custom-quaternary !t-rounded-md"
              onClick={() => {
                const scrollTo = formRef?.current.offsetTop;
                window.scrollTo({
                  top: scrollTo + 400,
                  behavior: 'smooth',
                });
              }}
            >
              {isGeneric ? t('CustomFormulaForm_Save') : t('CustomFormula_SaveAndSend')}
            </button>
          </div>
        )}
        <a
          href="https://wa.me/+4915156047816"
          target="_blank"
          rel="noreferrer"
          className="t-fixed t-bottom-32 sm:t-bottom-5 t-right-5 t-z-50"
          onClick={() => metaEvent('Contact')}
        >
          <img src={Whatsapp} alt="" className="t-h-10 t-w-10 md:t-h-15 md:t-w-15" />
        </a>
      </div>
      {isOmega3OnlyRecommendation ? (
        <div className="t-flex t-flex-col">
          <Omega3DifferenceSection />
          <Omega3BenefitsSection />
          <Omega3HowToSection />
          <Omega3IngredientsSection />
          <Omega3Reviews />
          <Omega3Faq />
        </div>
      ) : (
        <div className="t-flex t-flex-col t-gap-8 t-py-4 t-pb-40">
          <div className="t-w-full t-max-w-[500px] t-mx-auto !t-px-4">
            <HowToEnjoy />
          </div>
          <div className="t-w-full t-max-w-[500px] t-mx-auto !t-px-4">
            <ResultYouFeel />
          </div>
          <div className="t-w-full t-max-w-[1320px] t-mx-auto t-flex t-flex-col t-gap-4 t-overflow-auto t-px-4">
            <BoomTestimonials />
          </div>
          <div className="t-w-full t-max-w-[700px] t-mx-auto t-flex t-flex-col t-gap-4 t-overflow-auto md:t-min-h-[406px]">
            <h2 className="!t-text-center t-px-4">{t(BuiltByScience)}</h2>
            <ExpertSection lang={lang} />
            <HighStandards />
          </div>

          <div className="t-bg-gray-100 t-p-4">
            <div
              className="t-w-full t-max-w-[1320px] t-mx-auto t-flex t-flex-col t-gap-2 t-overflow-auto"
              ref={sectionRefMobile}
            >
              <h2 className="t-text-center !t-m-0">{t(CustomerReviews)}</h2>
              <p className="t-text-center !t-m-0 t-text-[#0E1311]">{t(CustomerReviewsDesc)}</p>
              <ReviewsMobile />
            </div>
          </div>
          <div className="t-max-w-[600px] t-mx-auto t-flex t-flex-col t-gap-4 t-px-4">
            <MakeChoice isProductPage />
          </div>
          <div className="t-bg-gray-100 t-p-4">
            <div className="t-w-full t-max-w-[600px] t-flex t-items-start t-justify-between t-gap-4 t-mx-auto">
              {ICONS_DATA.map((item) => (
                <div
                  className="t-flex-none t-w-1/6 t-flex t-flex-col t-items-center t-justify-center t-gap-1"
                  key={'unique-' + item.title}
                >
                  <img src={item.img} alt="" height={40} width={40} />
                  <p className="t-m-0 t-text-xs md:t-text-sm t-text-center t-text-secondary t-leading-4">
                    {item.title}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <div className="t-w-full t-max-w-[500px] t-mx-auto t-px-2">
            <h2 className="!t-text-center">{t(Faq)}</h2>
            <ProductSuggestionWhyRiise pickedFaqsShow />
          </div>
          <div className="t-max-w-[600px] t-mx-auto t-flex t-flex-col t-gap-4 t-px-4">
            <div>
              <h2 className="t-text-center !t-m-0">{t(HereMainHeading)}</h2>
              <h2 className="t-text-center !t-m-0">{t(HereMainHeading2)}</h2>
            </div>
            <HereForYou isProductPage />
          </div>
        </div>
      )}
      <IngredientsModal
        open={ingredientsModal}
        handleClose={() => setIngredientsModal(false)}
        data={data}
        allIngredients={allIngredients}
        name={customFormula?.customFormula?.name.trim()}
        onOpenEdit={
          isShareableFormula
            ? null
            : () => {
                setIngredientsModal(false);
                setEditIngredientsModal(true);
              }
        }
      />
      <EditFormulaModal
        open={editIngredientsModal}
        handleClose={() => {
          setEditIngredientsModal(false);
          setIngredientsModal(true);
          if (hasFormulaChanged) preloadCustomFormulaSubscriptionCheckouts({ force: true });
        }}
        data={data}
        setHasFormulaChanged={setHasFormulaChanged}
        setData={setData}
        setError={setError}
        setPrices={setPrices}
        ingredients={ingredients}
        pricesData={pricesData}
        hasFormulaChanged={hasFormulaChanged}
        setAddIngredientsModal={setAddIngredientsModal}
        allIngredients={allIngredients}
        onRemoveQuantity={onRemoveQuantity}
        onAddQuantity={onAddQuantity}
        prices={prices}
        removeIngredientFromBin={removeIngredientFromBin}
        onInputChange={onInputChange}
        onReset={() => {
          setHasFormulaChanged(false);
          setData(ingredients);
          setError({});
          setPrices(pricesData);
        }}
        name={customFormula?.customFormula?.name.trim()}
        isCustomFormula
      />
      <AddIngredientModal
        open={addIngredientsModal}
        handleClose={() => setAddIngredientsModal(false)}
        data={data}
        removeIngredient={removeIngredient}
        addIngredient={addIngredient}
        allIngredients={allIngredients}
      />
      {loading && <Loader />}
    </>
  );
};

export default CustomFormula;
